import { Component, JSX } from 'react';
import { OptionsMenu } from 'menus/optionsMenu/OptionsMenu';
import { WorkspaceOptions } from 'menus/optionsMenu/workspace/WorkspaceOptions';
import { ComponentFab as ComponentFabBase } from '../fab/componentFab/ComponentFab';
import { ArdoqId } from '@ardoq/api-types';
import { ContextShape } from '@ardoq/data-model';
import {
  Permissions,
  isDisabled,
} from 'streams/currentUserPermissions/permissionInterface';
import {
  action$,
  dispatchAction,
  extractPayload,
  ofType,
} from '@ardoq/rxbeach';
import { createEntities, saveEditedEntities } from 'appModelStateEdit/actions';
import { launchDeleteComponentsAndReferencesConfirmationDialogByIds } from 'components/Dialogs/confirmDeletion/confirmDeletion';
import { externallyManagedNotice$ } from 'externallyManaged/externallyManagedNotice$';
import { Features, hasFeature } from '@ardoq/features';
import { PropertiesEditorConnected as PropertiesEditor } from 'appModelStateEdit/propertiesEditor/PropertiesEditorConnected';
import {
  clearSubscriptions,
  subscribeToAction,
} from 'streams/utils/streamUtils';
import { notifyScenarioChanged } from 'streams/context/ContextActions';
import {
  context$,
  contextWorkspaceId$,
  contextWorkspaceOrComponentId$,
  contextReferenceId$,
} from 'streams/context/context$';
import { Subscription, map, withLatestFrom } from 'rxjs';
import { hideRightPane, showRightPane } from 'appContainer/actions';
import { isReadOnlyVisualizationNow } from 'scope/focusedChromeNow';
import {
  GetContentOptions,
  GetContentOptionsType,
} from 'appModelStateEdit/legacyTypes';
import { SidebarMenu } from '@ardoq/sidebar-menu';
import { ReferenceFab as ReferenceFabBase } from '../fab/referenceFab/ReferenceFab';
import { classes, Maybe } from '@ardoq/common-helpers';
import styled, { css } from 'styled-components';
import { debounce } from 'lodash';
import {
  trackRightPaneClosed,
  trackSaveComponentButtonClicked,
  trackCreateComponentButtonClicked,
  trackDeleteComponentButtonClicked,
} from './tracking';
import { components$ } from 'streams/components/components$';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import { isChildlessParent } from './isChildlessParent';
import { PropertyGroup } from 'appModelStateEdit/propertiesEditor/types';
import { type I18nLocale, t } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

const saveEditedEntitiesForPropertyGroups = () =>
  dispatchAction(saveEditedEntities());

const createEntitiesForPropertyGroups = (propertyGroups: PropertyGroup[]) =>
  dispatchAction(createEntities({ propertyGroups }));

const onSaveComponent = (isViewpointMode: boolean) => {
  trackSaveComponentButtonClicked(isViewpointMode);
  saveEditedEntitiesForPropertyGroups();
};

const onCreateComponent = (
  propertyGroups: PropertyGroup[],
  isViewpointMode: boolean
) => {
  trackCreateComponentButtonClicked(isViewpointMode);
  createEntitiesForPropertyGroups(propertyGroups);
};

const RightPaneContent = styled.div`
  display: flex;
  flex: 1;
  opacity: 0;
  width: 0;
  transition:
    width 0ms ease-in-out 100ms,
    opacity 100ms ease-in-out 100ms;
`;

const RightPaneElement = styled.div<{
  $isViewpointMode: boolean;
}>`
  max-width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex: 1;
  opacity: 1;
  transition: opacity 150ms ease-in-out;

  &.switchingContent {
    opacity: 0;
    transition: opacity 150ms ease-in-out;
  }

  &.open {
    ${({ $isViewpointMode }) =>
      !$isViewpointMode &&
      css`
        box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.12);
      `}

    ${RightPaneContent} {
      opacity: 1;
      width: 100%;
    }
  }
`;

const hidePane = () => dispatchAction(hideRightPane());

const getContent = (
  options: GetContentOptions,
  i18nLocale: I18nLocale,
  isViewpointMode: boolean,
  getIsExternallyManagedNoticeSuppressed: () => boolean,
  isParentChildless: boolean
) => {
  switch (options.type) {
    case GetContentOptionsType.MENU:
      return <OptionsMenu />;
    case GetContentOptionsType.WORKSPACE_AND_METAMODEL:
      return (
        <WorkspaceOptions
          headerTitle={t(i18nLocale, 'views:rightPane.settings', 'Settings')}
          onCloseClick={() => {
            trackRightPaneClosed(isViewpointMode);
            hidePane();
          }}
          closeButtonLabel={t(i18nLocale, 'views:rightPane.close', 'Close')}
          closeButtonAriaLabel={t(
            i18nLocale,
            'views:rightPane.closeAriaLabel',
            'Close sidebar'
          )}
        />
      );
    case GetContentOptionsType.CREATE_COMPONENT: {
      if (isParentChildless) {
        return (
          <SidebarMenu
            headerTitle={t(
              i18nLocale,
              'views:rightPane.createComponent',
              'Create component'
            )}
            onCloseClick={() => {
              trackRightPaneClosed(isViewpointMode);
              hidePane();
            }}
            emptyContentMessage={t(
              i18nLocale,
              'views:rightPane.cannotCreateChildComponent',
              "Can't create child component of selected node"
            )}
            closeButtonLabel={t(i18nLocale, 'views:rightPane.close', 'Close')}
            closeButtonAriaLabel={t(
              i18nLocale,
              'views:rightPane.closeAriaLabel',
              'Close sidebar'
            )}
          />
        );
      }
      return (
        <PropertiesEditor
          onClose={() => {
            trackRightPaneClosed(isViewpointMode);
            hidePane();
          }}
          onSave={propertyGroups =>
            onCreateComponent(propertyGroups, isViewpointMode)
          }
          trackingContext={GetContentOptionsType.CREATE_COMPONENT}
        />
      );
    }
    case GetContentOptionsType.COMPONENT_PROPERTIES: {
      const componentIds = options.componentIds;
      if (!componentIds) {
        return;
      }
      return (
        <PropertiesEditor
          onClose={() => {
            trackRightPaneClosed(isViewpointMode);
            hidePane();
          }}
          onSave={() => onSaveComponent(isViewpointMode)}
          onDelete={async () => {
            trackDeleteComponentButtonClicked(isViewpointMode);
            const confirmed =
              await launchDeleteComponentsAndReferencesConfirmationDialogByIds({
                componentIds,
                isExternallyManagedNoticeSuppressed:
                  getIsExternallyManagedNoticeSuppressed(),
              });

            if (confirmed) {
              hidePane();
            }
          }}
          trackingContext={GetContentOptionsType.COMPONENT_PROPERTIES}
        />
      );
    }
    case GetContentOptionsType.COMPONENT_STYLE: {
      return (
        <PropertiesEditor
          onClose={() => {
            trackRightPaneClosed(isViewpointMode);
            hidePane();
          }}
          onSave={() => onSaveComponent(isViewpointMode)}
          trackingContext={GetContentOptionsType.COMPONENT_STYLE}
        />
      );
    }
    case GetContentOptionsType.CREATE_REFERENCE: {
      return (
        <PropertiesEditor
          onClose={() => dispatchAction(hideRightPane())}
          onSave={createEntitiesForPropertyGroups}
          trackingContext={GetContentOptionsType.CREATE_REFERENCE}
        />
      );
    }
    case GetContentOptionsType.REFERENCE_PROPERTIES: {
      const referenceIds = options.referenceIds;
      if (!referenceIds) {
        return;
      }
      return (
        <PropertiesEditor
          onClose={() => {
            trackRightPaneClosed(isViewpointMode);
            hidePane();
          }}
          onSave={saveEditedEntitiesForPropertyGroups}
          onDelete={async () => {
            const confirmed =
              await launchDeleteComponentsAndReferencesConfirmationDialogByIds({
                referenceIds,
                isExternallyManagedNoticeSuppressed:
                  getIsExternallyManagedNoticeSuppressed(),
              });

            if (confirmed) {
              hidePane();
            }
          }}
          trackingContext={GetContentOptionsType.REFERENCE_PROPERTIES}
        />
      );
    }
  }
};
interface RightPaneProperties {
  focusDelay?: number;
  isViewpointMode: boolean;
}
interface RightPaneState {
  workspaceId: Maybe<ArdoqId>;
  content?: JSX.Element | null;
  showing?: GetContentOptionsType;
  switchingContent: boolean;
}
export class RightPane extends Component<RightPaneProperties, RightPaneState> {
  private boundClose?: VoidFunction;
  private onContextChange?: (context: ContextShape) => void;
  private workspaceChanged?: (workspaceId: Maybe<ArdoqId>) => void;
  private contentContainer: HTMLDivElement | null = null;
  private isExternallyManagedNoticeSuppressed = false;
  private subscriptions: Subscription[] = [];
  constructor(props: RightPaneProperties) {
    super(props);
    // TODO: This state should probably be removed in favor of using the rightControls$ stream.
    this.state = {
      workspaceId: null,
      content: undefined,
      showing: undefined,
      switchingContent: false,
    };
  }

  componentDidMount() {
    this.boundClose = this.close.bind(this);

    this.onContextChange = debounce((context: ContextShape) => {
      if (this.state.showing) {
        const editors = [
          GetContentOptionsType.CREATE_COMPONENT,
          GetContentOptionsType.COMPONENT_PROPERTIES,
          GetContentOptionsType.COMPONENT_STYLE,
          GetContentOptionsType.REFERENCE_PROPERTIES,
        ];
        const isShowingEditor = editors.indexOf(this.state.showing) !== -1;
        const disableEditWorkspace = isDisabled(Permissions.WORKSPACE_EDIT);

        const contextComponentId = context.componentId;
        if (isShowingEditor) {
          const referenceId = context.referenceId;
          if (referenceId) {
            dispatchAction(
              showRightPane({
                type: GetContentOptionsType.REFERENCE_PROPERTIES,
                referenceIds: [referenceId],
              })
            );
          } else if (
            this.state.showing === GetContentOptionsType.CREATE_COMPONENT
          ) {
            dispatchAction(
              showRightPane({
                type: this.state.showing,
                parentId: contextComponentId,
                workspaceId: context.workspaceId,
              })
            );
          } else if (contextComponentId) {
            if (this.state.showing === GetContentOptionsType.COMPONENT_STYLE) {
              dispatchAction(
                showRightPane({
                  type: GetContentOptionsType.COMPONENT_STYLE,
                  componentIds: [contextComponentId],
                })
              );
            } else {
              dispatchAction(
                showRightPane({
                  type: GetContentOptionsType.COMPONENT_PROPERTIES,
                  componentIds: [contextComponentId],
                })
              );
            }
          } else if (
            this.state.showing.match('-properties') &&
            !disableEditWorkspace
          ) {
            if (this.state.workspaceId) {
              dispatchAction(
                showRightPane({
                  type: GetContentOptionsType.MENU,
                })
              );
            }
          } else {
            dispatchAction(
              showRightPane({
                type: GetContentOptionsType.MENU,
              })
            );
          }
        } else if (this.state.showing === GetContentOptionsType.MENU) {
          dispatchAction(
            showRightPane({
              type: GetContentOptionsType.MENU,
            })
          );
        }
      }
    }, 100);

    this.workspaceChanged = workspaceId => {
      if (workspaceId) {
        this.setState({ workspaceId });
      } else {
        dispatchAction(hideRightPane());
      }
    };

    this.subscriptions.push(
      externallyManagedNotice$.subscribe(({ isSuppressed }) => {
        this.isExternallyManagedNoticeSuppressed = isSuppressed;
      }),
      action$
        .pipe(
          ofType(showRightPane),
          extractPayload(),
          withLatestFrom(components$, workspaceAndModel$),
          map(
            ([options, { byId }, { workspacesById, modelsById }]): [
              GetContentOptions,
              boolean,
            ] => [
              options,
              options.type === GetContentOptionsType.CREATE_COMPONENT &&
                isChildlessParent(
                  options.parentId,
                  byId,
                  workspacesById,
                  modelsById
                ),
            ]
          )
        )
        .subscribe(([options, isParentChildless]) => {
          this.open(options, isParentChildless);
        }),
      subscribeToAction(hideRightPane, this.boundClose),
      contextReferenceId$.subscribe(this.onContextChange),
      contextWorkspaceOrComponentId$.subscribe(this.onContextChange),
      action$
        .pipe(
          ofType(notifyScenarioChanged),
          withLatestFrom(context$),
          map(([, context]) => context)
        )
        .subscribe(this.onContextChange),
      contextWorkspaceId$.subscribe(({ workspaceId }) => {
        this.workspaceChanged?.(workspaceId);
      })
    );
  }

  open(options: GetContentOptions, isParentChildless: boolean) {
    if (isReadOnlyVisualizationNow()) {
      dispatchAction(hideRightPane());
      return;
    }
    // i18nLocale$.state OK here: imperative one-shot opener invoked from
    // `showRightPane` action; the content snapshot is rendered once per open.
    const content = getContent(
      options,
      i18nLocale$.state,
      this.props.isViewpointMode,
      () => this.isExternallyManagedNoticeSuppressed,
      isParentChildless
    );
    if (!content) {
      dispatchAction(hideRightPane());
      return;
    }

    const doSwitch = () => {
      this.setState(() => {
        return {
          showing: options.type,
          switchingContent: false,
          content,
        };
      });
    };

    if (this.state.showing) {
      this.setState({ switchingContent: true });
    }
    window.setTimeout(doSwitch, 150);
  }

  close() {
    this.setState({
      showing: undefined,
      content: undefined,
      switchingContent: false,
    });
  }

  render() {
    const hasReferenceFab = hasFeature(Features.REFERENCE_FAB_PROTOTYPE);
    return (
      <RightPaneElement
        $isViewpointMode={this.props.isViewpointMode}
        className={classes(
          'right-pane',
          this.state.content && 'open',
          this.state.switchingContent && 'switchingContent'
        )}
      >
        {hasReferenceFab && <ReferenceFabBase />}
        {this.props.isViewpointMode ? null : <ComponentFabBase />}
        <RightPaneContent
          className="right-pane-content"
          ref={contentContainer => {
            this.contentContainer = contentContainer;
          }}
        >
          {this.state.content}
        </RightPaneContent>
      </RightPaneElement>
    );
  }

  componentWillUnmount() {
    this.subscriptions = clearSubscriptions(this.subscriptions);
  }
}
